import { StrictMode } from 'react'
import { createRoot, type Root } from 'react-dom/client'
import { MemoryRouter } from 'react-router-dom'
import { AuthEmbedLayout, AuthRoutes } from './routes/AuthRoutes'
import embedCssUrl from './embed.css?url'
import './lib/amplify'
import { authRoutePathsBySegment, type AuthForm } from './routes/paths'
import type { AuthLayoutVariant } from './hooks/useAuthParams'
import { buildBrandingQueryString, type AuthBrandingOptions } from './lib/branding'
import { buildDisabledFlowsQueryString, type AuthFlow } from './lib/authFlows'
import { captureEmbedReactError, initializeEmbedSentry } from './sentry'

initializeEmbedSentry()

type EmbedForm = AuthForm

export type EmbedOptions = {
  target: string | Element
  form?: EmbedForm | string
  redirect?: string
  disableRedirectGate?: boolean
  layout?: AuthLayoutVariant
  autoLogin?: boolean
  registrationType?: string
  signupDescription?: string
  loginUrl?: string
  signupUrl?: string
  confirmSignUpUrl?: string
  forgotPasswordUrl?: string
  resetPasswordUrl?: string
  changePasswordUrl?: string
  mfaUrl?: string
  customChallengeUrl?: string
  logoutUrl?: string
  branding?: AuthBrandingOptions
  disabledFlows?: Array<AuthFlow | string>
}

const FORM_PATHS = authRoutePathsBySegment

const roots = new WeakMap<Element, Root>()
const containers = new WeakMap<Element, HTMLElement>()
const EMBED_STYLE_ID = 'auth-embed-stylesheet'
const EMBED_ROOT_ID = 'auth-embed-root'

function normalizeForm(value?: string) {
  if (!value) return 'login'
  const trimmed = value.trim().replace(/^\/+/, '')
  return trimmed || 'login'
}

function resolveTarget(target: string | Element) {
  if (typeof target !== 'string') return target
  const element = document.querySelector(target)
  if (!element) {
    throw new Error(`AuthEmbed target not found: ${target}`)
  }
  return element
}

function buildInitialEntry(
  form?: string,
  redirect?: string,
  layout?: AuthLayoutVariant,
  autoLogin?: boolean,
  registrationType?: string,
  linkOverrides?: {
    loginUrl?: string
    signupUrl?: string
    confirmSignUpUrl?: string
    forgotPasswordUrl?: string
    resetPasswordUrl?: string
    changePasswordUrl?: string
    mfaUrl?: string
    customChallengeUrl?: string
    logoutUrl?: string
  },
  signupDescription?: string,
  branding?: AuthBrandingOptions,
  disabledFlows?: Array<AuthFlow | string>,
) {
  const normalized = normalizeForm(form)
  const path = FORM_PATHS[normalized as EmbedForm]
  if (!path && form) {
    console.warn(`AuthEmbed received unrecognized form "${form}", defaulting to login.`)
  }
  const resolvedPath = path ?? '/login'
  const params = new URLSearchParams()
  if (redirect) {
    params.set('redirect', redirect)
  }
  if (layout) {
    params.set('layout', layout)
  }
  if (autoLogin) {
    params.set('autoLogin', '1')
  }
  if (registrationType) {
    params.set('registrationType', registrationType)
  }
  if (typeof signupDescription === 'string') {
    params.set('signupDescription', signupDescription)
  }
  if (linkOverrides) {
    Object.entries(linkOverrides).forEach(([key, value]) => {
      if (typeof value === 'string' && value.length > 0) {
        params.set(key, value)
      }
    })
  }
  const brandingParams = new URLSearchParams(buildBrandingQueryString(branding).replace(/^\?/, ''))
  brandingParams.forEach((value, key) => {
    params.set(key, value)
  })
  const disabledFlowParams = new URLSearchParams(buildDisabledFlowsQueryString(disabledFlows).replace(/^\?/, ''))
  disabledFlowParams.forEach((value, key) => {
    params.set(key, value)
  })
  const query = params.toString()
  return `${resolvedPath}${query ? `?${query}` : ''}`
}

function renderAuthForm(options: EmbedOptions) {
  const target = resolveTarget(options.target)
  const shadowRoot = ensureShadowRoot(target)
  const container = ensureEmbedContainer(target, shadowRoot)
  ensureEmbedStyles(shadowRoot)
  const initialEntry = buildInitialEntry(
    options.form,
    options.redirect,
    options.layout,
    options.autoLogin,
    options.registrationType,
    {
      loginUrl: options.loginUrl,
      signupUrl: options.signupUrl,
      confirmSignUpUrl: options.confirmSignUpUrl,
      forgotPasswordUrl: options.forgotPasswordUrl,
      resetPasswordUrl: options.resetPasswordUrl,
      changePasswordUrl: options.changePasswordUrl,
      mfaUrl: options.mfaUrl,
      customChallengeUrl: options.customChallengeUrl,
      logoutUrl: options.logoutUrl,
    },
    options.signupDescription,
    options.branding,
    options.disabledFlows,
  )
  const root = roots.get(target) ?? createRoot(container, {
    onCaughtError: captureEmbedReactError,
    onRecoverableError: captureEmbedReactError,
    onUncaughtError: captureEmbedReactError,
  })
  roots.set(target, root)

  root.render(
    <StrictMode>
      <MemoryRouter initialEntries={[initialEntry]}>
        <AuthRoutes layout={AuthEmbedLayout} disableRedirectGate={options.disableRedirectGate} />
      </MemoryRouter>
    </StrictMode>,
  )
}

function parseDisabledFlowsProps(props: Record<string, unknown>) {
  const rawDisabledFlows = props.disabledFlows
  if (Array.isArray(rawDisabledFlows)) {
    return rawDisabledFlows.filter((value): value is string => typeof value === 'string')
  }
  if (typeof rawDisabledFlows === 'string') {
    return rawDisabledFlows.split(',')
  }
  return undefined
}

function parseProps(raw: string | null) {
  if (!raw) return {}
  try {
    return JSON.parse(raw) as Record<string, unknown>
  } catch (error) {
    console.warn('AuthEmbed failed to parse data-props JSON', error)
    return {}
  }
}

function parseBrandingProps(props: Record<string, unknown>): AuthBrandingOptions | undefined {
  const rawBranding = props.branding
  const branding = rawBranding && typeof rawBranding === 'object'
    ? (rawBranding as Record<string, unknown>)
    : {}
  const primaryColor = typeof branding.primaryColor === 'string' ? branding.primaryColor : undefined
  const accentColor = typeof branding.accentColor === 'string' ? branding.accentColor : undefined
  const backgroundColor = typeof branding.backgroundColor === 'string' ? branding.backgroundColor : undefined
  const backgroundImageUrl =
    typeof branding.backgroundImageUrl === 'string' ? branding.backgroundImageUrl : undefined
  const colorMode = typeof branding.colorMode === 'string' ? branding.colorMode : undefined
  const favicon = typeof branding.favicon === 'string' ? branding.favicon : undefined
  const logoUrl = typeof branding.logoUrl === 'string' ? branding.logoUrl : undefined
  const logoAlt = typeof branding.logoAlt === 'string' ? branding.logoAlt : undefined
  const logoPlacement = typeof branding.logoPlacement === 'string' ? branding.logoPlacement : undefined
  const pageTitlePrefix = typeof branding.pageTitlePrefix === 'string' ? branding.pageTitlePrefix : undefined

  if (
    !primaryColor &&
    !accentColor &&
    !backgroundColor &&
    !backgroundImageUrl &&
    !colorMode &&
    !favicon &&
    !logoUrl &&
    !logoAlt &&
    !logoPlacement &&
    !pageTitlePrefix
  ) {
    return undefined
  }

  return {
    primaryColor,
    accentColor,
    backgroundColor,
    backgroundImageUrl,
    colorMode,
    favicon,
    logoUrl,
    logoAlt,
    logoPlacement,
    pageTitlePrefix,
  }
}

function renderFromElement(element: Element) {
  const form = element.getAttribute('data-auth-form') ?? undefined
  const props = parseProps(element.getAttribute('data-props'))
  const redirect = typeof props.redirect === 'string' ? props.redirect : undefined
  const disableRedirectGate = Boolean(props.disableRedirectGate)
  const autoLogin = Boolean(props.autoLogin)
  const registrationType = typeof props.registrationType === 'string' ? props.registrationType : undefined
  const signupDescription =
    typeof props.signupDescription === 'string' ? props.signupDescription : undefined
  const loginUrl = typeof props.loginUrl === 'string' ? props.loginUrl : undefined
  const signupUrl = typeof props.signupUrl === 'string' ? props.signupUrl : undefined
  const confirmSignUpUrl =
    typeof props.confirmSignUpUrl === 'string' ? props.confirmSignUpUrl : undefined
  const forgotPasswordUrl =
    typeof props.forgotPasswordUrl === 'string' ? props.forgotPasswordUrl : undefined
  const resetPasswordUrl =
    typeof props.resetPasswordUrl === 'string' ? props.resetPasswordUrl : undefined
  const changePasswordUrl =
    typeof props.changePasswordUrl === 'string' ? props.changePasswordUrl : undefined
  const mfaUrl = typeof props.mfaUrl === 'string' ? props.mfaUrl : undefined
  const customChallengeUrl =
    typeof props.customChallengeUrl === 'string' ? props.customChallengeUrl : undefined
  const logoutUrl = typeof props.logoutUrl === 'string' ? props.logoutUrl : undefined
  const branding = parseBrandingProps(props)
  const disabledFlows = parseDisabledFlowsProps(props)
  const rawLayout = props.layout ?? props.loginLayout
  const layout: AuthLayoutVariant | undefined =
    rawLayout === 'alternate' || rawLayout === 'default' ? rawLayout : undefined

  renderAuthForm({
    target: element,
    form,
    redirect,
    disableRedirectGate,
    autoLogin,
    layout,
    registrationType,
    signupDescription,
    loginUrl,
    signupUrl,
    confirmSignUpUrl,
    forgotPasswordUrl,
    resetPasswordUrl,
    changePasswordUrl,
    mfaUrl,
    customChallengeUrl,
    logoutUrl,
    branding,
    disabledFlows,
  })
}

function renderAll() {
  const nodes = document.querySelectorAll('[data-auth-form]')
  nodes.forEach((element) => renderFromElement(element))
}

window.AuthEmbed = {
  render: renderAuthForm,
  renderAll,
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', renderAll, { once: true })
} else {
  renderAll()
}

function ensureShadowRoot(target: Element) {
  if (target.shadowRoot) return target.shadowRoot
  return target.attachShadow({ mode: 'open' })
}

function ensureEmbedContainer(target: Element, shadowRoot: ShadowRoot) {
  const existing = containers.get(target)
  if (existing && shadowRoot.contains(existing)) return existing
  const container = document.createElement('div')
  container.id = EMBED_ROOT_ID
  shadowRoot.appendChild(container)
  containers.set(target, container)
  return container
}

function ensureEmbedStyles(shadowRoot: ShadowRoot) {
  if (shadowRoot.getElementById(EMBED_STYLE_ID)) return
  const link = document.createElement('link')
  link.id = EMBED_STYLE_ID
  link.rel = 'stylesheet'
  link.href = embedCssUrl
  shadowRoot.appendChild(link)
}

export const __testing = {
  buildInitialEntry,
  normalizeForm,
}
